// src/components/PdfModalPdfjs.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
// import { getFunctions, httpsCallable } from "firebase/functions"; // 🔥 제거
import { getAuth } from "firebase/auth"; // 🔥 추가: getAuth import
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/build/pdf";

// ✨ 변경: 외부 CDN 대신 로컬 public 폴더에서 로드하도록 경로 변경
GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

export default function PdfModalPdfjs({ open, onClose, filePath, sid, title }) {
  // --- 설정 ---
  const MIN_ZOOM_HARD_CAP = 0.1; // 절대 하한
  const MAX_ZOOM = 1.0;          // 가로맞춤 상한

  // --- refs ---
  const holderRef = useRef(null); // 뷰어 컨테이너(overlay 기준)
  const stageRef  = useRef(null); // 세로 이동(translateY) 레이어
  const scaledRef = useRef(null); // 가로 중앙 + scale 레이어
  const canvasRef = useRef(null); // PDF 캔버스

  const lastKeyRef   = useRef(null);
  const renderedRef  = useRef(false);
  const minScaleRef  = useRef(MIN_ZOOM_HARD_CAP); // 동적 최소 배율 저장
  const baseCssWRef  = useRef(0); // zoom=1 기준 CSS 폭
  const baseCssHRef  = useRef(0); // zoom=1 기준 CSS 높이

  // --- state ---
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1.0);

  // ✅ 초기 치수 측정 완료 여부 (첫 진입 − 잠김 이슈 해결용)
  const [metricsReady, setMetricsReady] = useState(false);

  // 입력 상태
  const touchState = useRef({ isDragging: false, lastY: 0, translateY: 0 });
  const mouseState = useRef({ isDragging: false, lastY: 0 });

  // 모달 열릴 때 측정 플래그 초기화
  useEffect(() => {
    if (open) setMetricsReady(false);
  }, [open]);

  // ---------- 컨테이너 내부 사이즈(패딩 제외) ----------
  const getInnerBox = useCallback(() => {
    const el = holderRef.current;
    if (!el) return { innerW: 600, innerH: 400 };
    const cs = getComputedStyle(el);
    const padX = parseFloat(cs.paddingLeft || "0") + parseFloat(cs.paddingRight || "0");
    const padY = parseFloat(cs.paddingTop  || "0") + parseFloat(cs.paddingBottom || "0");
    return {
      innerW: Math.max(320, el.clientWidth  - padX),
      innerH: Math.max(300, el.clientHeight - padY),
    };
  }, []);

  // ---------- 동적 최소 배율(세로 맞춤) ----------
  const computeMinZoom = useCallback(() => {
    const { innerH } = getInnerBox();
    const baseH = baseCssHRef.current || 1;
    const fitHeight = innerH / baseH; // 이 값 이상이면 scaledH >= innerH
    return Math.max(MIN_ZOOM_HARD_CAP, Math.min(fitHeight, MAX_ZOOM));
  }, [getInnerBox]);

  // ---------- 세로 이동 클램프 ----------
  const clampTranslateY = useCallback((ty, z) => {
    const { innerH } = getInnerBox();
    const scaledH = baseCssHRef.current * z;
    if (scaledH <= innerH + 0.5) return 0; // 한 화면이면 이동 불필요
    const minY = innerH - scaledH; // 음수(최하단)
    return Math.max(minY, Math.min(0, ty));
  }, [getInnerBox]);

  // ---------- 변환 적용: stage(Y), scaled(X+scale) ----------
  const applyTransforms = useCallback((z, ty, withTransition = false) => {
    const stage  = stageRef.current;
    const scaled = scaledRef.current;
    if (!stage || !scaled) return;

    // 1) 세로 이동
    stage.style.setProperty("transform", `translateY(${ty}px)`, "important");
    stage.style.setProperty("transition", withTransition ? "transform .16s ease" : "none", "important");

    // 2) 가로 중앙 + 스케일
    const { innerW } = getInnerBox();
    const scaledW = baseCssWRef.current * z;
    const tx = (innerW - scaledW) / 2; // 항상 가운데
    scaled.style.setProperty("transform-origin", "top left", "important");
    scaled.style.setProperty("transform", `translateX(${tx}px) scale(${z})`, "important");
    scaled.style.setProperty("transition", withTransition ? "transform .16s ease" : "none", "important");
  }, [getInnerBox]);

  const syncApply = useCallback((z, ty, withTransition = false) => {
    applyTransforms(z, ty, withTransition);
  }, [applyTransforms]);

  // ---------- 줌 변경(현재 시야 중앙 고정) + 동적 min 반영 ----------
  const handleZoomChange = useCallback((nextZoomRaw) => {
    const dynMin = computeMinZoom();
    minScaleRef.current = dynMin;

    const newZoom = Math.min(MAX_ZOOM, Math.max(dynMin, nextZoomRaw));
    const { innerH } = getInnerBox();

    const oldScaledH = baseCssHRef.current * zoom;
    const newScaledH = baseCssHRef.current * newZoom;

    const viewportCenter = innerH / 2;
    const oldTY = touchState.current.translateY;
    const oldDocY = viewportCenter - oldTY; // 문서 좌표
    const ratio  = oldScaledH > 0 ? Math.min(1, Math.max(0, oldDocY / oldScaledH)) : 0.5;
    const newDocY = ratio * newScaledH;

    let newTY = viewportCenter - newDocY;
    newTY = clampTranslateY(newTY, newZoom);

    touchState.current.translateY = newTY;
    setZoom(newZoom);
    syncApply(newZoom, newTY, true);
  }, [zoom, computeMinZoom, clampTranslateY, getInnerBox, syncApply]);

  const handleZoomIn  = useCallback(() => handleZoomChange(Math.round((zoom + 0.1) * 100) / 100), [zoom, handleZoomChange]);
  const handleZoomOut = useCallback(() => handleZoomChange(Math.round((zoom - 0.1) * 100) / 100), [zoom, handleZoomChange]);

  // ---------- 터치/마우스 드래그 ----------
  const onTouchStart = useCallback((e) => {
    const t = e.touches;
    if (t.length !== 1) return;
    touchState.current.isDragging = true;
    touchState.current.lastY = t[0].clientY;
  }, []);
  const onTouchMove = useCallback((e) => {
    if (!touchState.current.isDragging) return;
    const t = e.touches;
    if (t.length !== 1) return;
    const dy = t[0].clientY - touchState.current.lastY;
    let ty = touchState.current.translateY + dy;
    ty = clampTranslateY(ty, zoom);
    touchState.current.translateY = ty;
    touchState.current.lastY = t[0].clientY;
    syncApply(zoom, ty, false);
  }, [zoom, clampTranslateY, syncApply]);
  const onTouchEnd = useCallback(() => { touchState.current.isDragging = false; }, []);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    mouseState.current.isDragging = true;
    mouseState.current.lastY = e.clientY;
  }, []);
  const onMouseMove = useCallback((e) => {
    if (!mouseState.current.isDragging) return;
    const dy = e.clientY - mouseState.current.lastY;
    let ty = touchState.current.translateY + dy;
    ty = clampTranslateY(ty, zoom);
    touchState.current.translateY = ty;
    mouseState.current.lastY = e.clientY;
    syncApply(zoom, ty, false);
  }, [zoom, clampTranslateY, syncApply]);
  const onMouseUp = useCallback(() => { mouseState.current.isDragging = false; }, []);

  // ---------- 휠 스크롤(Y 이동) ----------
  useEffect(() => {
    if (!open || !holderRef.current) return;
    const el = holderRef.current;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) return; // (전역에서 페이지 줌 차단)
      e.preventDefault();
      let ty = touchState.current.translateY - e.deltaY;
      ty = clampTranslateY(ty, zoom);
      touchState.current.translateY = ty;
      syncApply(zoom, ty, false);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel, { passive: false });
  }, [open, zoom, clampTranslateY, syncApply]);

  // ---------- 전역 키보드 ----------
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose();
      if ((e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "P")) { e.preventDefault(); e.stopPropagation(); }
      const unit = 60, pageUnit = 400;
      let moved = false;
      let ty = touchState.current.translateY;
      switch (e.key) {
        case "ArrowDown": ty = clampTranslateY(ty - unit, zoom); moved = true; break;
        case "ArrowUp":   ty = clampTranslateY(ty + unit, zoom); moved = true; break;
        case "PageDown":  ty = clampTranslateY(ty - pageUnit, zoom); moved = true; break;
        case "PageUp":    ty = clampTranslateY(ty + pageUnit, zoom); moved = true; break;
        case "Home":      ty = clampTranslateY(0, zoom); moved = true; break;
        case "End": {
          const { innerH } = getInnerBox();
          const scaledH = baseCssHRef.current * zoom;
          ty = clampTranslateY(innerH - scaledH, zoom);
          moved = true;
          break;
        }
        default: break;
      }
      if (moved) {
        e.preventDefault();
        touchState.current.translateY = ty;
        syncApply(zoom, ty, false);
      }
    };

    // 브라우저 페이지 줌 차단(Ctrl/⌘ + Wheel)
    const preventPageZoom = (e) => {
      if (e.ctrlKey || e.metaKey) { e.preventDefault(); e.stopPropagation(); }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("keydown", onKey, { capture: true });
    window.addEventListener("wheel", preventPageZoom, { passive: false, capture: true });

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("keydown", onKey, { capture: true });
      window.removeEventListener("wheel", preventPageZoom, { capture: true });
    };
  }, [open, onMouseMove, onMouseUp, clampTranslateY, syncApply, getInnerBox, zoom, loading, onClose]);

  // ---------- 리사이즈: 동적 min 재계산 + 현재 줌/위치 보정 ----------
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      const dynMin = computeMinZoom();
      minScaleRef.current = dynMin;
      if (zoom < dynMin) {
        // 현재 시야 중앙 기준으로 dynMin까지 확대
        const { innerH } = getInnerBox();
        const oldScaledH = baseCssHRef.current * zoom;
        const newScaledH = baseCssHRef.current * dynMin;
        const viewportCenter = innerH / 2;
        const oldTY = touchState.current.translateY;
        const ratio = oldScaledH > 0 ? (viewportCenter - oldTY) / oldScaledH : 0.5;
        let newTY = viewportCenter - ratio * newScaledH;
        newTY = clampTranslateY(newTY, dynMin);
        touchState.current.translateY = newTY;
        setZoom(dynMin);
        syncApply(dynMin, newTY, false);
      } else {
        // 위치만 경계 보정
        const ty = clampTranslateY(touchState.current.translateY, zoom);
        touchState.current.translateY = ty;
        syncApply(zoom, ty, false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, computeMinZoom, clampTranslateY, syncApply, getInnerBox, zoom]);

  // ---------- 페이지 렌더 ----------
  const renderPage = useCallback(async (doc, num) => {
    if (!doc || !canvasRef.current || !holderRef.current || renderedRef.current) return;

    try {
      renderedRef.current = true;

      const page = await doc.getPage(num);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d", { alpha: false });

      const { innerW } = getInnerBox();
      const baseViewport = page.getViewport({ scale: 1 });

      // zoom=1에서 가로 꽉차게
      const fitWidthScale = innerW / baseViewport.width;
      const cssW = innerW;
      const cssH = baseViewport.height * fitWidthScale;

      baseCssWRef.current = cssW;
      baseCssHRef.current = cssH;

      // CSS 기준 크기
      canvas.style.width  = `${Math.round(cssW)}px`;
      canvas.style.height = `${Math.round(cssH)}px`;

      // 실제 렌더 해상도(고품질)
      const isMobile = window.innerWidth <= 768;
      const q = isMobile ? 3.0 : 4.0;
      const renderScale = fitWidthScale * q;
      const renderViewport = page.getViewport({ scale: renderScale });

      canvas.width  = Math.floor(renderViewport.width);
      canvas.height = Math.floor(renderViewport.height);

      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({
        canvasContext: ctx,
        viewport: renderViewport,
        intent: "display",
        renderInteractiveForms: false
      }).promise;

      // ✅ 동적 최소 배율 계산 후 초기 상태
      const dynMin = computeMinZoom();
      minScaleRef.current = dynMin;

      const initZoom = Math.min(MAX_ZOOM, Math.max(dynMin, 1.0));
      touchState.current.translateY = 0;
      setZoom(initZoom);
      syncApply(initZoom, 0, false);

      // ✅ 치수 측정 완료 표시 + 다음 프레임에서 하한 재보정
      setMetricsReady(true);
      requestAnimationFrame(() => {
        const dynMin2 = computeMinZoom();
        minScaleRef.current = dynMin2;
        setZoom(prev => {
          const nz = Math.max(prev, dynMin2);
          let ty = clampTranslateY(touchState.current.translateY, nz);
          touchState.current.translateY = ty;
          syncApply(nz, ty, false);
          return nz;
        });
      });
    } catch (err) {
      console.error("PDF 렌더 오류:", err);
    } finally {
      setTimeout(() => { renderedRef.current = false; }, 100);
    }
  }, [getInnerBox, computeMinZoom, clampTranslateY, syncApply]);

  const renderFirstPage = useCallback(async (doc) => { if (doc) await renderPage(doc, 1); }, [renderPage]);

  // ---------- PDF 로드 ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!open || !filePath || !sid) { renderedRef.current = false; return; }
      setLoading(true);
      setErr(null);
      renderedRef.current = false;

      try {
        const key = `${filePath}::${sid}`;
        if (pdfDoc && lastKeyRef.current === key) {
          setLoading(false);
          await renderFirstPage(pdfDoc);
          return;
        }

        // 🔥 변경: HTTP Request (fetch)로 전환
        const auth = getAuth();
        if (!auth.currentUser) throw new Error("인증 오류: 사용자가 로그인되지 않았습니다. (토큰 획득 불가)");
        
        const idToken = await auth.currentUser.getIdToken(); 
        
        const res = await fetch("/api/pdf-binary", { // 🔥 새로운 엔드포인트 사용
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                // Authorization 헤더에 ID Token 추가 (보안 유지)
                "Authorization": `Bearer ${idToken}`, 
            },
            // 요청 본문에 filePath와 sid를 JSON으로 전송
            body: JSON.stringify({ filePath, sid }), 
        });
        
        if (!res.ok) {
            const errorText = await res.text();
            // 서버의 HTTP 응답 상태 코드에 따른 에러 처리
            if (res.status === 401) throw new Error("인증 실패: 다시 로그인해주세요.");
            if (res.status === 403) throw new Error("권한 없음: 학수번호를 확인해주세요.");
            // 500 에러 처리
            throw new Error(`PDF 로드 실패 (${res.status} ${res.statusText}). 서버 메시지: ${errorText.substring(0, 100)}...`);
        }

        // 🔥 Binary 응답 (ArrayBuffer) 처리: Base64 디코딩/인코딩 제거
        const arrayBuffer = await res.arrayBuffer(); 
        const bytes = new Uint8Array(arrayBuffer); 

        const task = getDocument({ data: bytes, useSystemFonts: true, disableFontFace: false });
        const doc = await task.promise;
        if (cancelled) return;

        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setPageNum(1);
        lastKeyRef.current = key;

        await renderFirstPage(doc);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "PDF 로드 실패");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; renderedRef.current = false; };
  }, [open, filePath, sid, pdfDoc, renderFirstPage]);

  // ---------- 버튼 비활성화 플래그 ----------
  const currentMinZoom = Math.max(minScaleRef.current, computeMinZoom());
  // ✅ 측정 전엔 임시로 − 잠금 → 첫 진입도 자연스럽게 풀림
  const minusDisabled  = !metricsReady || zoom <= currentMinZoom + 1e-6;
  const plusDisabled   = zoom >= MAX_ZOOM - 1e-6;

  if (!open) return null;

  return (
    <div
      style={backdropStyle}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !loading && !mouseState.current.isDragging) onClose(); }}
      className="pdf-modal-root"
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}>
        {/* 헤더 */}
        <div style={headerStyle}>
          <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {title || "특별해설"}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={handleZoomOut}
              disabled={minusDisabled}
              style={{ ...zoomBtnStyle, opacity: minusDisabled ? 0.3 : 1, cursor: minusDisabled ? "not-allowed" : "pointer" }}
              aria-label="축소"
              title={minusDisabled ? "더 이상 축소할 수 없어요" : "축소"}
            >
              −
            </button>

            <span style={{ fontSize: 12, fontWeight: 600, minWidth: 45, textAlign: "center" }}>
              {Math.round(zoom * 100)}%
            </span>

            <button
              onClick={handleZoomIn}
              disabled={plusDisabled}
              style={{ ...zoomBtnStyle, opacity: plusDisabled ? 0.3 : 1, cursor: plusDisabled ? "not-allowed" : "pointer" }}
              aria-label="확대"
              title={plusDisabled ? "더 이상 확대할 수 없어요" : "확대"}
            >
              +
            </button>
          </div>

          <button onClick={onClose} style={closeBtnStyle} aria-label="닫기">✕</button>
        </div>

        {/* 뷰어: 로딩 오버레이 + stage(Y) → scaled(X+scale) → canvas */}
        <div ref={holderRef} style={viewerStyle}>
          {/* ✅ 로딩 오버레이: 항상 뷰어 정중앙 */}
          {loading && !err && (
            <div style={loadingOverlayStyle}>
              <div style={loadingBoxStyle}>
                <div style={loadingSpinnerStyle} />
                <div style={{ fontSize: 16, fontWeight: 700, color: "#e5e7eb" }}>불러오는 중</div>
              </div>
            </div>
          )}

          <div ref={stageRef} style={stageStyle}>
            <div ref={scaledRef} style={scaledStyle}>
              {err && (
                <div style={{ ...centerStyle, color: "var(--bad)" }}>
                  {String(err)}
                </div>
              )}
              {!loading && !err && (
                <canvas
                  ref={canvasRef}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  onMouseDown={onMouseDown}
                  onMouseLeave={onMouseUp}
                  style={{
                    display: "block",
                    userSelect: "none",
                    maxWidth: "100%",
                    maxHeight: "none",
                    objectFit: "contain",
                    imageRendering: "high-quality",
                    touchAction: "none",
                    cursor: mouseState.current.isDragging || touchState.current.isDragging ? 'grabbing' : 'grab',
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* 페이지 네비(여러 페이지일 때) */}
        {numPages > 1 && !loading && (
          <div style={footerStyle}>
            <button
              style={navBtnStyle}
              onClick={async () => {
                if (renderedRef.current || !pdfDoc || pageNum <= 1) return;
                const prev = pageNum - 1;
                setPageNum(prev);
                await renderPage(pdfDoc, prev);
              }}
            >← 이전</button>
            <span style={{ fontWeight: 700 }}>Page {pageNum} / {numPages}</span>
            <button
              style={navBtnStyle}
              onClick={async () => {
                if (renderedRef.current || !pdfDoc || pageNum >= numPages) return;
                const next = pageNum + 1;
                setPageNum(next);
                await renderPage(pdfDoc, next);
              }}
            >다음 →</button>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media print { .pdf-modal-root { display: none !important; } }
      `}</style>
    </div>
  );
}

/* ---------------- styles ---------------- */
const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const modalStyle = {
  width: "min(95vw, 900px)",
  height: "min(80vh, 800px)",
  background: "#1c1f24",
  color: "#e5e7eb",
  border: "1px solid #2d333b",
  borderRadius: 12,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: "0 15px 50px rgba(0,0,0,.5)",
  position: "relative"
};

const headerStyle = {
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 12px",
  borderBottom: "1px solid #2d333b",
  background: "linear-gradient(#1c1f24, #1a1d22)",
  flexShrink: 0,
  gap: 12,
};

const closeBtnStyle = {
  border: "1px solid #2d333b",
  borderRadius: 6,
  background: "transparent",
  padding: "4px 10px",
  cursor: "pointer",
  color: "#e5e7eb",
  fontSize: 16,
  lineHeight: 1,
};

const zoomBtnStyle = {
  border: "1px solid #2d333b",
  borderRadius: 6,
  background: "rgba(126,162,255,.12)",
  padding: "4px 10px",
  cursor: "pointer",
  color: "#e5e7eb",
  fontSize: 18,
  lineHeight: 1,
  fontWeight: "bold",
  minWidth: 32,
  height: 32,
};

const viewerStyle = {
  flex: 1,
  background: "#111",
  position: "relative",  // ✅ 오버레이 기준
  overflow: "hidden",
  padding: "15px",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  touchAction: "none",
  overscrollBehavior: "contain",
};

// 세로 이동 전용(stage)
const stageStyle = {
  position: "relative",
  willChange: "transform",
  minHeight: 0,
};

// 가로 중앙 + scale 전용(scaled)
const scaledStyle = {
  position: "relative",
  willChange: "transform",
};

// 에러 메시지 센터링(스케일 트리 내부에서도 중앙)
const centerStyle = {
  position: "absolute",
  inset: 0,
  display: "grid",
  placeItems: "center",
};

// 로딩 오버레이(뷰어 정중앙)
const loadingOverlayStyle = {
  position: "absolute",
  inset: 0,
  display: "grid",
  placeItems: "center", // ✅ 정확히 중앙(X/Y)
  zIndex: 5,
  background: "transparent",
  pointerEvents: "auto", // 로딩 중 인터랙션 막으려면 'auto', 허용하려면 'none'
};

const loadingBoxStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 12,
};

const loadingSpinnerStyle = {
  width: 50,
  height: 50,
  border: "4px solid #333",
  borderTop: "4px solid var(--primary)",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

const footerStyle = {
  borderTop: "1px solid #2d333b",
  padding: "8px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "rgb(21, 29, 54)",
  fontSize: 14,
  flexShrink: 0,
};

const navBtnStyle = {
  border: "1px solid #2d333b",
  background: "transparent",
  color: "#e5e7eb",
  borderRadius: 8,
  padding: "8px 12px",
  cursor: "pointer",
  fontWeight: 600,
};
